<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Vendor Payment" />

		<div class="container-fluid">
			<div class="row mb-3">
				<div class="col-12">
					<router-link :to="{ name: 'vendor_credits' }" class="btn btn-light btn-sm">
						<i class="icofont icofont-arrow-left me-1"></i> Back to vendor credits
					</router-link>
				</div>
			</div>

			<div class="row">
				<div class="col-lg-4 mb-3">
					<div class="card h-100 border-primary">
						<div class="card-body">
							<h6 class="text-muted text-uppercase small mb-2">Vendor</h6>
							<p class="mb-1 fw-bold">{{ vendorName }}</p>
							<p class="mb-2 text-muted small">Vendor ID: {{ vendorId }}</p>
						</div>
					</div>
				</div>
				<div class="col-lg-8 mb-3">
					<div class="card h-100 bg-light">
						<div class="card-body">
							<div class="row text-center">
								<div class="col-md-4">
									<small class="text-muted d-block">Pending invoices (total)</small>
									<span class="fw-bold text-danger">₹{{ formatMoney(totalPendingAmount) }}</span>
								</div>
								<div class="col-md-4">
									<small class="text-muted d-block">Due amount</small>
									<span class="fw-bold text-danger">₹{{ formatMoney(dueAmount) }}</span>
								</div>
								<div class="col-md-4">
									<small class="text-muted d-block">This payment amount</small>
									<span class="fw-bold text-primary fs-5">₹{{ formatMoney(form.amount) }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="card">
				<div class="card-body">
					<div v-if="loadingInitial" class="text-center py-5">
						<div class="spinner-border text-primary" role="status"></div>
						<p class="mt-2 text-muted small">Loading pending invoices…</p>
					</div>

					<form v-else @submit.prevent="submitPayment">
						<div class="row">
							<div class="col-md-6 form-group mt-2">
								<label class="control-label">Payment for<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.payment_for" @change="onPaymentForChange">
									<option value="">— Select —</option>
									<option value="1">Invoice payment</option>
									<option value="2">Deposit only</option>
								</select>
								<div v-if="errors.payment_for" class="text-danger small mt-1">{{ errors.payment_for }}</div>
							</div>
						</div>

						<!-- Invoice allocations -->
						<div v-if="form.payment_for === '1'" class="mt-3 position-relative">
							<div
								v-if="refreshingInvoices"
								class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
								style="z-index: 2"
							>
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
							</div>

							<h6 class="border-bottom pb-2">Apply to pending invoices</h6>
							<p class="small text-muted">Enter amount to apply for each invoice. Sum should match total payment below.</p>

							<div class="row mb-2">
								<div class="col-md-8 col-lg-6">
									<input
										type="text"
										class="form-control form-control-sm"
										placeholder="Search invoices (invoice #) — updates as you type"
										v-model="invoiceKeyword"
										@input="onInvoiceKeywordInput"
									/>
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th class="text-start">Invoice #</th>
											<th>Date</th>
											<th>Pending</th>
											<th style="min-width: 140px">Apply (₹)</th>
										</tr>
									</thead>
									<tbody v-if="invoiceRows.length">
										<tr v-for="row in invoiceRows" :key="'i-' + row.id" align="center">
											<td class="text-start small">{{ row.invoice_number || "—" }}</td>
											<td class="small">{{ row.invoice_date ? $filters.formatDate(row.invoice_date) : "—" }}</td>
											<td class="fw-bold text-danger">₹{{ formatMoney(row.pending_amount) }}</td>
											<td>
												<input
													type="number"
													class="form-control form-control-sm"
													min="0"
													step="0.01"
													:placeholder="'max ' + formatMoney(row.pending_amount)"
													v-model="invoiceAmounts[row.id]"
													@input="syncAmountFromAllocations"
												/>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="4" class="text-center text-muted py-3">No pending invoices</td>
										</tr>
									</tbody>
								</table>
							</div>

							<nav v-if="invoicePagination.length" class="d-flex justify-content-end">
								<ul class="pagination pagination-sm mb-0">
									<li
										class="page-item"
										v-for="(page, idx) in invoicePagination"
										:key="'ip-' + idx"
										:class="{ disabled: !page.url, active: page.active }"
									>
										<a class="page-link" href="#" @click.prevent="onInvoicePage(page)">{{ formatPaginationLabel(page.label) }}</a>
									</li>
								</ul>
							</nav>

							<p class="small mb-0 mt-2">
								Allocated: <strong>₹{{ formatMoney(sumInvoiceAllocated) }}</strong>
							</p>
						</div>

						<hr class="my-4" />

						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Total amount (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.amount" placeholder="0.00" />
								<small v-if="form.payment_for === '1'" class="text-muted">Updates when you change invoice allocation amounts.</small>
								<div v-if="errors.amount" class="text-danger small mt-1">{{ errors.amount }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.payment_type" @change="onPaymentTypeChange">
									<option value="">— Select —</option>
									<option value="1">Cash</option>
									<option value="2">Online</option>
									<option value="3">Both</option>
								</select>
								<div v-if="errors.payment_type" class="text-danger small mt-1">{{ errors.payment_type }}</div>
							</div>
						</div>

						<div class="row" v-if="form.payment_type === '1' || form.payment_type === '3'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Cash in hand (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_in_hand" />
								<div v-if="errors.cash_in_hand" class="text-danger small mt-1">{{ errors.cash_in_hand }}</div>
							</div>
						</div>
						<div class="row" v-if="form.payment_type === '2' || form.payment_type === '3'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Cash online (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_online" />
								<div v-if="errors.cash_online" class="text-danger small mt-1">{{ errors.cash_online }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Bank account<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.bank_account_id">
									<option value="">— Select bank —</option>
									<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
								<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Submit payment" }}
								</button>
								<router-link :to="{ name: 'vendor_credits' }" class="btn btn-light ms-2">Cancel</router-link>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "VendorPayment",
	data() {
		return {
			token: "",
			loadingInitial: true,
			refreshingInvoices: false,
			invoiceSearchTimer: null,
			submitting: false,
			bankAccounts: [],
			totalPendingAmount: "0",
			invoiceRows: [],
			invoicePagination: [],
			invoiceKeyword: "",
			invoiceLimit: 50,
			invoiceAmounts: {},
			errors: {},
			form: {
				amount: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				bank_account_id: "",
				payment_id: "",
				payment_for: "",
			},
		};
	},
	computed: {
		vendorId() {
			return parseInt(this.$route.params.id, 10) || null;
		},
		vendorName() {
			return this.$route.query.vendor_name || (this.invoiceRows?.[0]?.vendor_name || ("Vendor #" + this.vendorId));
		},
		dueAmount() {
			return this.$route.query.due_amount != null ? String(this.$route.query.due_amount) : this.totalPendingAmount;
		},
		sumInvoiceAllocated() {
			return this.sumMap(this.invoiceAmounts);
		},
	},
	created() {
		if (!this.vendorId) {
			this.$swal("Error", "Invalid vendor", "error");
			this.$router.push({ name: "vendor_credits" });
			return;
		}
		this.init();
	},
	beforeUnmount() {
		if (this.invoiceSearchTimer) clearTimeout(this.invoiceSearchTimer);
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			await Promise.all([this.fetchBankAccounts(), this.fetchPendingInvoices()]);
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : (data?.data || []);
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		pendingInvoicesUrl() {
			const p = new URLSearchParams();
			p.set("vendor_id", String(this.vendorId));
			p.set("limit", String(this.invoiceLimit));
			p.set("keyword", this.invoiceKeyword || "");
			return `/staffs/pendingInvoices?${p.toString()}`;
		},

		fetchPendingInvoices(pageUrl) {
			this.getToken();
			const isFirstLoad = this.loadingInitial;
			if (!isFirstLoad) this.refreshingInvoices = true;
			const url = pageUrl || this.pendingInvoicesUrl();
			return this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					if (res.data.status !== true && res.data.status !== "true") {
						this.invoiceRows = [];
						this.invoicePagination = [];
						this.totalPendingAmount = "0";
						return;
					}
					const d = res.data.data || {};
					this.totalPendingAmount = d.totalPendingAmount ?? "0";
					const pag = d.pendingInvoices;
					this.invoiceRows = pag?.data || [];
					this.invoicePagination = pag?.links || [];
					this.mergeInvoiceAmountMap();
				})
				.catch((err) => {
					console.error(err);
					this.$swal("Error", err.response?.data?.message || "Failed to load pending invoices", "error");
					this.invoiceRows = [];
					this.invoicePagination = [];
				})
				.finally(() => {
					this.loadingInitial = false;
					this.refreshingInvoices = false;
				});
		},

		onInvoiceKeywordInput() {
			if (this.invoiceSearchTimer) clearTimeout(this.invoiceSearchTimer);
			this.invoiceSearchTimer = setTimeout(() => {
				this.invoiceSearchTimer = null;
				this.fetchPendingInvoices();
			}, 400);
		},

		formatPaginationLabel(label) {
			if (label == null || label === "") return "";
			const el = document.createElement("textarea");
			el.innerHTML = String(label);
			return el.value;
		},

		onInvoicePage(page) {
			if (!page.url) return;
			this.fetchPendingInvoices(page.url);
		},

		mergeInvoiceAmountMap() {
			for (const row of this.invoiceRows) {
				if (this.invoiceAmounts[row.id] === undefined) {
					this.invoiceAmounts[row.id] = "";
				}
			}
		},

		onPaymentForChange() {
			this.errors = {};
			if (this.form.payment_for === "1") {
				this.syncAmountFromAllocations();
			} else if (this.form.payment_for === "2") {
				this.form.amount = "";
				this.invoiceAmounts = {};
				this.mergeInvoiceAmountMap();
			}
		},

		onPaymentTypeChange() {
			this.errors = {};
			if (this.form.payment_type === "1") {
				this.form.cash_online = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			} else if (this.form.payment_type === "2") {
				this.form.cash_in_hand = "";
			}
		},

		syncAmountFromAllocations() {
			if (this.form.payment_for !== "1") return;
			const s = this.sumMap(this.invoiceAmounts);
			this.form.amount = s > 0 ? s.toFixed(2) : this.form.amount;
		},

		sumMap(obj) {
			let t = 0;
			for (const k of Object.keys(obj)) {
				const n = parseFloat(obj[k]);
				if (!isNaN(n) && n > 0) t += n;
			}
			return Math.round(t * 100) / 100;
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		buildPairs(amountMap) {
			const pairs = [];
			for (const k of Object.keys(amountMap)) {
				const id = parseInt(k, 10);
				const v = parseFloat(amountMap[k]);
				if (!isNaN(id) && !isNaN(v) && v > 0) pairs.push([id, v]);
			}
			pairs.sort((a, b) => a[0] - b[0]);
			return {
				ids: pairs.map((p) => p[0]),
				amounts: pairs.map((p) => p[1]),
			};
		},

		validate() {
			this.errors = {};
			const eps = 0.02;
			const amount = parseFloat(this.form.amount);
			if (isNaN(amount) || amount < 0) {
				this.errors.amount = "Enter a valid amount";
				return false;
			}
			if (!this.form.payment_for) {
				this.errors.payment_for = "Select payment for";
				return false;
			}
			if (!this.form.payment_type) {
				this.errors.payment_type = "Select payment type";
				return false;
			}

			const pt = this.form.payment_type;
			if (pt === "1" || pt === "3") {
				const c = parseFloat(this.form.cash_in_hand);
				if (isNaN(c) || c < 0) {
					this.errors.cash_in_hand = "Enter cash amount";
					return false;
				}
			}
			if (pt === "2" || pt === "3") {
				const o = parseFloat(this.form.cash_online);
				if (isNaN(o) || o < 0) {
					this.errors.cash_online = "Enter online amount";
					return false;
				}
				if (!String(this.form.payment_id || "").trim()) {
					this.errors.payment_id = "Payment ID is required for online payment";
					return false;
				}
				if (!this.form.bank_account_id) {
					this.errors.bank_account_id = "Select bank account";
					return false;
				}
			}

			if (this.form.payment_for === "1") {
				const { ids, amounts } = this.buildPairs(this.invoiceAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one invoice", "warning");
					return false;
				}
				const sum = amounts.reduce((a, b) => a + b, 0);
				if (Math.abs(sum - amount) > eps) {
					this.$swal(
						"Validation",
						`Total amount (₹${amount.toFixed(2)}) must match sum of invoice allocations (₹${sum.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			}

			if (pt === "1" || pt === "3") {
				const cash = parseFloat(this.form.cash_in_hand) || 0;
				const online = parseFloat(this.form.cash_online) || 0;
				if (Math.abs(cash + online - amount) > eps) {
					this.$swal(
						"Validation",
						`Cash + online (₹${(cash + online).toFixed(2)}) should equal total amount (₹${amount.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			}
			if (pt === "2") {
				const online = parseFloat(this.form.cash_online) || 0;
				if (Math.abs(online - amount) > eps) {
					this.$swal("Validation", "Online amount must equal total amount for online-only payment.", "warning");
					return false;
				}
			}

			return true;
		},

		submitPayment() {
			if (this.submitting) return;
			if (!this.validate()) return;
			this.submitting = true;
			this.getToken();

			const payload = {
				vendor_id: this.vendorId,
				amount: parseFloat(this.form.amount),
				payment_type: parseInt(this.form.payment_type, 10),
				payment_for: parseInt(this.form.payment_for, 10),
			};

			const pt = this.form.payment_type;
			if (pt === "2" || pt === "3") {
				payload.cash_online = parseFloat(this.form.cash_online) || 0;
				payload.payment_id = String(this.form.payment_id || "").trim();
				payload.bank_account_id = this.form.bank_account_id ? parseInt(this.form.bank_account_id, 10) : null;
			} else {
				payload.cash_online = null;
				payload.payment_id = null;
				payload.bank_account_id = null;
			}
			if (pt === "1" || pt === "3") {
				payload.cash_in_hand = parseFloat(this.form.cash_in_hand) || 0;
			} else {
				payload.cash_in_hand = null;
			}

			if (this.form.payment_for === "1") {
				const { ids, amounts } = this.buildPairs(this.invoiceAmounts);
				payload.invoice_ids = ids;
				payload.invoice_amounts = amounts;
			}

			this.$axios
				.post("/staffs/vendorPayment", payload, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					if (res.data.status === true || res.data.status === "true") {
						this.$swal("Success", res.data.message || "Payment recorded", "success");
						this.$router.push({ name: "vendor_credits" });
					} else {
						this.$swal("Error", res.data.message || "Failed", "error");
					}
				})
				.catch((err) => {
					const msg = err.response?.data?.message;
					const errs = err.response?.data?.errors;
					if (errs && typeof errs === "object") {
						const first = Object.values(errs)[0];
						this.$swal("Error", Array.isArray(first) ? first[0] : msg || "Validation failed", "error");
					} else {
						this.$swal("Error", msg || "Request failed", "error");
					}
				})
				.finally(() => {
					this.submitting = false;
				});
		},
	},
};
</script>

